import AppCommandBar from 'app/AppCommandBar'
import { FlexSpinner } from 'components'
import { ViewAuthorizer } from 'components/AccessDenied'
import { StickyPane } from 'components/StickyPane'
import { FormikActions } from 'formik'
import {
  GlobalOptionsForm,
  GlobalOptionsFormValues,
} from 'forms/KeywordListForm'
import {
  SingleValueOptionForm,
  SingleValueOptionFormType,
} from 'forms/KeywordListForm/SingleValueOptionForm'
import { dbg } from 'helpers'
import { useEditCommand } from 'hooks/useCommand'
import { useFormikPanelState } from 'hooks/useFormikPanelState'
import useSelection from 'hooks/useSelection'
import {
  GlobalSettingType,
  OptionsSetting,
  SingleValueSetting,
  UserRole,
} from 'model'
import { FormikPanel } from 'panels'
import * as React from 'react'
import { AdminKeywordList } from './AdminKeywordList'
import {
  useCreateOrUpdateOptionMutation,
  useCreateOrUpdateSingleValueMutation,
  useGetKeywordListsQuery,
} from './AdminKeywordsView.generated'

const AdminKeywordsListView: React.FunctionComponent = () => {
  const { data, loading, error, refetch } = useGetKeywordListsQuery()
  const [updateOptions] = useCreateOrUpdateOptionMutation()
  const [updateSingleValue] = useCreateOrUpdateSingleValueMutation()

  const items =
    data && data.globalSettings
      ? (data.globalSettings.filter(
          (elem) =>
            elem.__typename === 'OptionsSetting' ||
            elem.__typename === 'SingleValueSetting'
        ) as (OptionsSetting | SingleValueSetting)[])
      : []

  const [selection, selectedItems] = useSelection<
    OptionsSetting | SingleValueSetting
  >({
    getKey: (item) => item._id!,
  })

  const [optionsPanelState, showOptionsPanel, hideOptionsPanel] =
    useFormikPanelState()
  const [singleValuePanelState, showSingleValuePanel, hideSingleValuePanel] =
    useFormikPanelState()

  const handleSubmitOptionsSetting = async (
    values: GlobalOptionsFormValues,
    formikActions: FormikActions<GlobalOptionsFormValues>
  ) => {
    formikActions.setSubmitting(true)

    const { errors: errors2 } = await updateOptions({
      variables: {
        type: values.type as GlobalSettingType,
        options: values.options,
      },
    })
    if (errors2) {
      dbg(errors2, 'ERRORS ON SUBMIT')
    }
    formikActions.setSubmitting(false)
    selection.setAllSelected(false)
    refetch()
    hideOptionsPanel()
  }

  const handleSubmitSingleValueSetting = async (
    values: SingleValueOptionFormType,
    formikActions: FormikActions<SingleValueOptionFormType>
  ) => {
    formikActions.setSubmitting(true)

    const { errors: errors2 } = await updateSingleValue({
      variables: {
        type: values.type as GlobalSettingType,
        value: values.value,
      },
    })
    if (errors2) {
      dbg(errors2, 'ERRORS ON SUBMIT')
    }
    formikActions.setSubmitting(false)
    selection.setAllSelected(false)
    refetch()
    hideSingleValuePanel()
  }

  useEditCommand(
    { name: 'edit', count: selectedItems.length },
    {
      priority: 20,
      onClick: () => {
        const setting = selectedItems[0]
        if (setting.__typename === 'OptionsSetting') {
          showOptionsPanel({
            options: setting.options,
            type: setting.type,
          } as OptionsSetting)
        }
        if (setting.__typename === 'SingleValueSetting') {
          showSingleValuePanel({
            value: setting.value,
            type: setting.type,
          } as SingleValueSetting)
        }
      },
    },
    [selectedItems]
  )

  return (
    <FlexSpinner loading={loading} error={error}>
      <AdminKeywordList
        items={items}
        selection={selection}
        onItemInvoked={() => {
          const setting = selectedItems[0]
          if (setting.__typename === 'OptionsSetting') {
            showOptionsPanel({
              options: setting.options,
              type: setting.type,
            } as OptionsSetting)
          }
          if (setting.__typename === 'SingleValueSetting') {
            showSingleValuePanel({
              value: setting.value,
              type: setting.type,
            } as SingleValueSetting)
          }
        }}
      />

      <FormikPanel {...optionsPanelState} onSubmit={handleSubmitOptionsSetting}>
        <GlobalOptionsForm />
      </FormikPanel>
      <FormikPanel
        {...singleValuePanelState}
        onSubmit={handleSubmitSingleValueSetting}
      >
        <SingleValueOptionForm />
      </FormikPanel>
    </FlexSpinner>
  )
}

export const AdminKeywordsView: React.FC = () => (
  <ViewAuthorizer roles={[UserRole.SystemAdministrator]}>
    <StickyPane>
      <AppCommandBar />
      <AdminKeywordsListView />
    </StickyPane>
  </ViewAuthorizer>
)
